import { useState, useEffect } from "react";

import { Amplify } from "aws-amplify";
import {
  fetchAuthSession,
  signOut,
  signInWithRedirect,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { AuthContext } from "./AuthContext";
import { ReactNode } from "react";
import { User } from "./AuthState";
import { useAuthenticator } from "@aws-amplify/ui-react";

type AmplifyConfig = Parameters<typeof Amplify.configure>[0];

interface CognitoAuthProviderProps {
  amplifyConfig: AmplifyConfig;
  children: ReactNode;
}

export function CognitoAuthProvider(props: CognitoAuthProviderProps) {
  Amplify.configure(props.amplifyConfig);

  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const isAuthenticated = authStatus === "authenticated";

  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User>();

  const checkAuthenticated = async () => {
    const data = await fetchAuthSession();
    if (!data.tokens?.accessToken) {
      setUser(undefined);
      return;
    }
    const userAttributes = await fetchUserAttributes();
    const givenName = userAttributes.given_name ?? "";
    const familyName = userAttributes.family_name ?? "";
    setUser({
      cognitoUser: { accessToken: data.tokens?.accessToken?.toString() ?? "" },
      name: `${familyName} ${givenName}`,
    });
  };

  useEffect(() => {
    setIsLoading(true);
    checkAuthenticated().finally(() => {
      setIsLoading(false);
    });
  }, [authStatus]);

  return (
    <AuthContext
      value={{
        isAuthenticated,
        isLoading,
        signIn: signInWithRedirect,
        signOut,
        user,
      }}
    >
      {props.children}
    </AuthContext>
  );
}
